import { useState } from 'react';

import { ControlledMenu, MenuButton, MenuItem, useClick } from '@szhsin/react-menu';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import { resetTradeScreenLayout } from 'actions/layoutSetup';
import { updateTradePreference } from 'actions/trade';
import { isAuthenticated, updateUserPreference } from 'actions/user';
import Modal from 'components/modal';
import { WalletConvertIcon } from 'components/primaryHeader/partials/walletMenu/walletIcons';
import ToggleThemeSwitch from 'components/toggleThemeSwitch';
import { IS_INDIAN_EXCHANGE, MARGIN_TYPES } from 'constants/constants';
import { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { noop } from 'helpers/utils';
import useLockedBody from 'hooks/useLockedBody';
import i18n from 'i18n/config';
import { tradeScreenLayoutStatusSelector } from 'selectors/layoutSetupSelector';
import {
  allPrefrencesSelector,
  userMarginModeSelector,
  userSelector,
} from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import { LearnMoreIcon, SettingsIcon } from 'styles/assets/icons';
import AntToggle from 'UIKit/AntToggle';
import Box from 'UIKit/Box';
import ToggleButtonSwitch from 'UIKit/ToggleSwitchButton';

import styles from './index.module.scss';

const defaultAutoTopUpLabel = i18n.t('headerFooter:header.settings.defaultAutoTopUp');
const positionForSocialShareLabel = i18n.t(
  'headerFooter:header.settings.positionForSocialShare'
);
const confirmationMessagesLabel = i18n.t(
  'headerFooter:header.settings.confirmationMessages'
);
const emailAlertsLabel = i18n.t('headerFooter:header.settings.emailAlerts');
const themeLabel = i18n.t('headerFooter:header.settings.theme');
const tradingTerminalLayoutLabel = i18n.t(
  'trading:header.changeLayout.tradingTerminalLayout'
);
const restoreDefaultLabel = i18n.t('trading:header.changeLayout.restoreDefault');
const payInDetoLabel = i18n.t('headerFooter:header.settings.payInDeto');
const learnMoreLabel = i18n.t('headerFooter:header.settings.learnMore');
const goHereLabel = i18n.t('headerFooter:header.settings.goHere');
const forFinerControlLabel = i18n.t('headerFooter:header.settings.forFinerControl');
const showAllButtonLabel = i18n.t('headerFooter:header.settings.showAllButton');
const showWatchlistButtonLabel = i18n.t(
  'headerFooter:header.settings.showWatchlistButton'
);
const manageAllPreferencesLabel = i18n.t(
  'headerFooter:header.settings.manageAllPreferences'
);
const featureForVerifiedLabel = i18n.t('headerFooter:header.settings.featureForVerified');
const getVerifiedLabel = i18n.t('headerFooter:header.settings.getVerified');
const cancelLabel = i18n.t('headerFooter:header.settings.cancel');

const PreferencesSettingsMenu = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const allPreferences = useSelector(state => allPrefrencesSelector(state));
  const user = useSelector(state => userSelector(state));
  const isLoggedin = isAuthenticated(user);
  const tradeScreenLayoutStatus = useSelector(tradeScreenLayoutStatusSelector);
  const marginMode = useAppSelector(userMarginModeSelector);
  const [isWatchlistActive, setWatchlistActive] = useState(
    user.preferences.showOnlyFavsOnTicker
  );

  const [showModal, setShowModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const onMenuClose = () => setIsMenuOpen(false);
  const { onClick, onMouseDown } = useClick(isMenuOpen, setIsMenuOpen);
  useLockedBody(isMenuOpen);

  const showAutoTopUp = marginMode === MARGIN_TYPES.ISOLATED;

  const {
    deto_for_commission: detoForCommission,
    limitOrder,
    marketOrder,
    stopLimitOrder,
    stopMarketOrder,
    trailingStopOrder,
    takeProfitLimitOrder,
    takeProfitMarketOrder,
    fatFingerProtection,
    alwaysShowOnMobile,
    email_preferences: emailPreferences,
    vip_level: vipLevel,
  } = allPreferences || {};

  const prefrencesMenu = [
    {
      label: defaultAutoTopUpLabel,
      key: 'default_auto_topup',
      hasLearnMore: false,
      isTradeprefrence: true,
      hasFinerControl: false,
      isLabelShown: showAutoTopUp,
    },

    {
      label: positionForSocialShareLabel,
      key: 'positionForSocialShare',
      hasLearnMore: false,
      isTradeprefrence: false,
      hasFinerControl: false,
      isLabelShown: true,
    },
    {
      label: confirmationMessagesLabel,
      key: 'confirmation_messages',
      hasLearnMore: false,
      isTradeprefrence: false,
      hasFinerControl: true,
      isLabelShown: true,
    },
    {
      label: emailAlertsLabel,
      key: 'email_alerts',
      hasLearnMore: false,
      isTradeprefrence: true,
      hasFinerControl: true,
      isLabelShown: true,
    },
  ];

  const getSwitchBoolValue = key => {
    if (key === 'confirmation_messages') {
      return (
        limitOrder &&
        marketOrder &&
        stopLimitOrder &&
        stopMarketOrder &&
        trailingStopOrder &&
        takeProfitLimitOrder &&
        takeProfitMarketOrder &&
        fatFingerProtection &&
        alwaysShowOnMobile
      );
    }
    if (key === 'email_alerts') {
      const isEmailAlertOn = !Object.values(emailPreferences).some(value => !value);
      return isEmailAlertOn;
    }
    return allPreferences[key];
  };

  const showTickerData = key => {
    if (key === 'All') {
      setWatchlistActive(false);
      dispatch(updateUserPreference({ preferences: { showOnlyFavsOnTicker: false } }));
    } else {
      setWatchlistActive(true);
      dispatch(updateUserPreference({ preferences: { showOnlyFavsOnTicker: true } }));
    }
  };

  const onToggle = (event, prefrences) => {
    const {
      target: { checked, name },
    } = event;
    if (prefrences.key === 'confirmation_messages') {
      return dispatch(
        updateUserPreference({
          preferences: {
            limitOrder: checked,
            marketOrder: checked,
            stopLimitOrder: checked,
            stopMarketOrder: checked,
            trailingStopOrder: checked,
            fatFingerProtection: checked,
            alwaysShowOnMobile: checked,
            takeProfitLimitOrder: checked,
            takeProfitMarketOrder: checked,
            [name]: checked,
          },
        })
      );
    }

    if (prefrences.isTradeprefrence) {
      if (prefrences.key === 'email_alerts') {
        const emailPrefPayload = Object.keys(emailPreferences).reduce(
          (acc, key) => ({ ...acc, [key]: checked }),
          {}
        );
        dispatch(updateTradePreference({ email_preferences: emailPrefPayload }));
      } else {
        dispatch(updateTradePreference({ [name]: checked }));
      }
    } else {
      dispatch(updateUserPreference({ preferences: { [name]: checked } }));
    }
    return null;
  };

  const modalStyles = {
    content: {
      top: '50%',
      left: '50%',
      position: 'inherit',
      transform: '',
      width: 'fit-content',
    },
  };

  const onDetoCommissionToggle = ({ target: { checked } }) => {
    if (user.is_kyc_done) {
      dispatch(
        updateTradePreference({
          deto_for_commission: checked,
        })
      );
    } else {
      setShowModal(true);
    }
  };

  const resetTradeLayout = e => {
    e.preventDefault();
    if (tradeScreenLayoutStatus) {
      dispatch(resetTradeScreenLayout());
    }
  };

  const getVerifiedClick = () => {
    history.push(routes.account.identityVerification);
    setShowModal(false);
  };

  const handleOnGoHereClick = key => {
    if (key) {
      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.PREFERENCES_CLICKED, {
        source: MIXPANEL_SOURCES.SETTINGS_GO_HERE,
        key,
      });
    }
  };

  const manageAllPreferencesClick = () => {
    history.push(routes.account.preferences);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.PREFERENCES_CLICKED, {
      source: MIXPANEL_SOURCES.SETTINGS_MANAGE_ALL_PREFERENCES,
    });
  };

  const handlePreferenceSettingsIconClick = () => {
    trackMixpanelEvent('Preferences Clicked - All Pages');
    onClick();
  };

  return (<>
    <Box renderWhen={isMenuOpen} className={styles.overlay} />
    <MenuButton
      className={cn(styles.settingsMenuButton, {
        [styles.active]: isMenuOpen,
      })}
      onClick={handlePreferenceSettingsIconClick}
      onMouseDown={onMouseDown}
      data-testid="settings-panel"
    >
      <SettingsIcon width={18} height={18} className={styles.settingsMenuIcon} />
    </MenuButton>
    <ControlledMenu
      data-testid="settings-panel-menu"
      state={isMenuOpen ? 'open' : 'closed'}
      onClose={onMenuClose}
      className={styles.preferenceMenu}
    >
      <MenuItem
        data-testid="preferences-menu-item-theme"
        className={styles['preference-menu-item']}
        onClick={e => {
          const event = e;
          event.keepOpen = true;
        }}
      >
        <span className={styles.label}>{themeLabel}</span>
        <ToggleThemeSwitch />
      </MenuItem>

      <MenuItem
        data-testid="preferences-menu-item-reset-trade-layout"
        className={styles['preference-menu-item']}
        onClick={e => {
          const event = e;
          event.keepOpen = true;
        }}
      >
        <span className={styles.label}>{tradingTerminalLayoutLabel}</span>

        <span className={styles['toggle-switch']}>
          <div
            data-testid="resetTradeLayout"
            className={`${tradeScreenLayoutStatus ? styles.active : ''} ${
              styles['convert-button']
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={noop}
            onClick={resetTradeLayout}
            disabled={!tradeScreenLayoutStatus}
          >
            <WalletConvertIcon className={styles['convert-icon']} />

            {restoreDefaultLabel}
          </div>
        </span>
      </MenuItem>

      {isLoggedin && vipLevel === 0 && !IS_INDIAN_EXCHANGE && (
        <MenuItem
          data-testid="preferences-menu-item-trading-fee-link"
          className={styles['preference-menu-item']}
          onClick={e => {
            const event = e;
            event.keepOpen = true;
          }}
        >
          <span className={styles.label}>
            {payInDetoLabel}
            <a
              data-testid="trading-fee-link"
              className={styles['learn-more']}
              href="https://www.delta.exchange/blog/support/paying-trading-fees-in-deto/"
              target="_blank"
              rel="noreferrer"
            >
              {learnMoreLabel}
              <LearnMoreIcon />
            </a>
          </span>

          <span className={styles['toggle-switch']}>
            <AntToggle
              data-testid="detoCommission"
              checked={detoForCommission}
              onChange={event => {
                onDetoCommissionToggle(event);
              }}
              name="deto_for_commission"
            />
          </span>
        </MenuItem>
      )}

      {isLoggedin &&
        prefrencesMenu.map(
          prefrences =>
            prefrences.isLabelShown && (
              <MenuItem
                data-testid="preferences-menu-item-interest-bearing-account-link"
                key={prefrences.key}
                className={`${styles['preference-menu-item']} ${
                  prefrences.key === 'toggleButton' && styles['ticker-item-container']
                }`}
                onClick={e => {
                  const event = e;
                  event.keepOpen = true;
                }}
              >
                <span
                  className={`${styles.label} ${
                    prefrences.key === 'toggleButton' && styles['ticker-options-label']
                  }`}
                >
                  {prefrences.label}
                  {prefrences.hasLearnMore && (
                    <a
                      data-testid="interestBearingAccountLink"
                      className={styles['learn-more']}
                      href="https://www.delta.exchange/interest-bearing-account/"
                      target="_blank"
                      rel="noreferrer"
                      data-palette="PreferencesSettingsMenu">
                      {learnMoreLabel} <LearnMoreIcon />
                    </a>
                  )}
                  {prefrences.hasFinerControl && (
                    <span
                      className={styles['finer-control-text']}
                      data-palette="PreferencesSettingsMenu">
                      {' '}
                      <Link
                        to={{
                          pathname: routes.account.preferences,
                          state: { from: history.location },
                        }}
                        data-testid="go-here-button"
                        onClick={() => handleOnGoHereClick(prefrences.key)}
                      >
                        {goHereLabel}
                      </Link>{' '}
                      {forFinerControlLabel}
                    </span>
                  )}
                </span>
                {prefrences.key === 'toggleButton' ? (
                  <ToggleButtonSwitch
                    data-testid="showWatchListBtn"
                    leftButtonName={showAllButtonLabel}
                    rightButtonName={showWatchlistButtonLabel}
                    leftClickHandler={() => showTickerData('All')}
                    rightClickHandler={() => showTickerData('Watchlist')}
                    activeButtonName={
                      isWatchlistActive ? showWatchlistButtonLabel : showAllButtonLabel
                    }
                  />
                ) : (
                  <span
                    className={styles['toggle-switch']}
                    data-palette="PreferencesSettingsMenu">
                    <AntToggle
                      data-testid={`antSwitch ${prefrences.key}`}
                      checked={getSwitchBoolValue(prefrences.key)}
                      onChange={event => onToggle(event, prefrences)}
                      name={prefrences.key}
                    />
                  </span>
                )}
              </MenuItem>
            )
        )}

      {isLoggedin && (
        <MenuItem
          data-testid="manage-all-preferences-button"
          className={styles['manage-prefrences-button']}
          onClick={manageAllPreferencesClick}
        >
          {manageAllPreferencesLabel}
        </MenuItem>
      )}
    </ControlledMenu>
    <Modal isOpen={showModal} onRequestClose={() => null} style={modalStyles}>
      <Box className={`${styles.modalWrapper} ${styles[`deto-commission`]}`}>
        <div className={styles.body}>{featureForVerifiedLabel}</div>
        <div className={`${styles.buttons}`}>
          <button
            data-testid="preference-settings-menu-modal-get-verified-button"
            type="button"
            className={styles.confirm_button}
            onClick={getVerifiedClick}
          >
            {getVerifiedLabel}
          </button>
          <button
            data-testid="preference-settings-menu-modal-cancel-button"
            type="button"
            className={styles.cancel_button}
            onClick={() => setShowModal(false)}
          >
            {cancelLabel}
          </button>
        </div>
      </Box>
    </Modal>
  </>);
};

export default PreferencesSettingsMenu;
